// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useRef, useEffect } from 'react';
import { AppIcon } from '@loa/ui-components';

type MoreOptionsButtonProps = Readonly<{
  onEdit: () => void;
  onDelete?: () => void;
  className?: string;
}>;

export function MoreOptionsButton({ onEdit, onDelete, className = '' }: MoreOptionsButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        data-testid="more-options-button"
        onClick={() => setOpen(!open)}
        className="cursor-pointer flex items-center justify-center w-[40px] h-[40px] rounded-lg border border-purple-primary bg-transparent hover:bg-lilac-06 transition-colors"
      >
        <AppIcon name="more" size={24} color="#2D0A6C" />
      </button>

      {open && (
        <div
          className="absolute top-[calc(100%+8px)] right-0 w-[208px] inline-flex p-md flex-col items-start gap-sm rounded-lg bg-white z-50"
          style={{ boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.10)' }}
        >
          {/* Edit policy */}
          <button
            type="button"
            className="flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer hover:bg-[#F5F5F7] transition-colors bg-transparent border-0 text-left"
            onClick={() => { setOpen(false); onEdit(); }}
          >
            <AppIcon name="edit" size={16} className="flex-shrink-0" />
            <span className="text-black font-sans text-body-02 font-normal leading-normal">Edit policy</span>
          </button>


          {/* Delete policy */}
          {onDelete && (
          <button
            type="button"
            className="flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer hover:bg-[#F5F5F7] transition-colors bg-transparent border-0 text-left"
            onClick={() => { setOpen(false); onDelete(); }}
          >
            <AppIcon name="trash" size={16} className="flex-shrink-0" />
            <span className="text-black font-sans text-body-02 font-normal leading-normal">Delete policy</span>
          </button>
          )}
        </div>
      )}
    </div>
  );
}
